import { Money } from '../ui/Money';
import React, { useEffect, useRef } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';

const ExitInfoSection = ({ formData, handleChange }) => {
  const manualOverrideRef = useRef(false);
  const aedText = (v) => `AED ${Math.round(Number(v || 0)).toLocaleString('en-US')}`;
  const aed = (v) => <Money value={v} />;
  const status = formData.employment_status;

  // Death — read-only next-of-kin payout summary.
  if (status === 'Death') {
    const nok = formData.next_of_kin_payment;
    return (
      <div>
        <h3 className="text-lg font-semibold mb-4 text-rose-600">Death — Next of Kin Payment</h3>
        {!nok ? (
          <p className="text-sm text-gray-500">No next-of-kin payment recorded yet. Re-select "Death" in Employment Status to record the settlement.</p>
        ) : (
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 space-y-3" data-testid="nok-payment-summary">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div><span className="text-gray-500">Recipient name:</span> <span className="font-medium">{nok.name || '—'}</span></div>
              <div><span className="text-gray-500">Relation:</span> <span className="font-medium">{nok.relation || '—'}</span></div>
              <div><span className="text-gray-500">Emirates ID:</span> <span className="font-medium">{nok.emirates_id || '—'}</span></div>
              <div><span className="text-gray-500">Cheque no.:</span> <span className="font-medium">{nok.cheque_no || '—'}</span></div>
              <div><span className="text-gray-500">Court order no.:</span> <span className="font-medium">{nok.court_order_no || '—'}</span></div>
              <div><span className="text-gray-500">Paid on:</span> <span className="font-medium">{nok.paid_at ? new Date(nok.paid_at).toLocaleDateString('en-GB') : '—'}</span></div>
            </div>
            <div className="border-t border-rose-200 pt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div><p className="text-gray-500 text-xs">EOSB</p><p className="font-semibold">{aed(nok.eosb_amount)}</p></div>
              <div><p className="text-gray-500 text-xs">Leave Salary</p><p className="font-semibold">{aed(nok.leave_salary)}</p></div>
              <div><p className="text-gray-500 text-xs">Pending Salary</p><p className="font-semibold">{aed(nok.pending_salary)}</p></div>
              <div><p className="text-gray-500 text-xs">Total Paid</p><p className="font-bold text-rose-700">{aed(nok.total_amount)}</p></div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Absconding / In Prison / Visa Cancelled — read-only visa freeze / vault note.
  if (status === 'Absconding' || status === 'In Prison' || status === 'Visa Cancelled Outside Country') {
    return (
      <div>
        <h3 className="text-lg font-semibold mb-4 text-amber-600">Visa & Vault Status</h3>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm space-y-1.5" data-testid="visa-vault-summary">
          <div><span className="text-gray-500">Status:</span> <span className="font-medium">{status}</span></div>
          <div><span className="text-gray-500">Last working date:</span> <span className="font-medium">{formData.last_working_date || '—'}</span></div>
          {formData.visa_freeze_until && (
            <div><span className="text-gray-500">Visa frozen until:</span> <span className="font-medium">{formData.visa_freeze_until}</span></div>
          )}
          {formData.visa_cancelled_date && (
            <div><span className="text-gray-500">Visa cancelled on:</span> <span className="font-medium">{formData.visa_cancelled_date}</span></div>
          )}
          <p className="text-xs text-amber-700 pt-1">Dues for this employee are held in the <b>Staff Vault</b> ("with company"). Manage them from the Staff Vault page.</p>
        </div>
      </div>
    );
  }

  if (status !== 'Resigned' && status !== 'Terminated') {
    return null;
  }

  const sourceDate = formData.employment_status === 'Resigned'
    ? formData.resignation_date
    : formData.termination_date;

  const calculateLastWorkingDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  };

  const autoDate = sourceDate ? calculateLastWorkingDate(sourceDate) : '';
  const isManual = formData._lwd_manual === true;

  const handleSourceDateChange = (field, value) => {
    handleChange(field, value);
    if (!formData._lwd_manual) {
      const calc = calculateLastWorkingDate(value);
      handleChange('last_working_date', calc);
    }
  };

  const handleManualToggle = (checked) => {
    handleChange('_lwd_manual', checked);
    if (!checked && sourceDate) {
      handleChange('last_working_date', calculateLastWorkingDate(sourceDate));
    }
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4 text-red-600">Exit Information</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {formData.employment_status === 'Resigned' && (
          <div>
            <Label>Resignation Date</Label>
            <Input 
              type="date" 
              value={formData.resignation_date} 
              onChange={(e) => handleSourceDateChange('resignation_date', e.target.value)} 
              data-testid="employee-resignation-date-input"
            />
          </div>
        )}
        {formData.employment_status === 'Terminated' && (
          <div>
            <Label>Termination Date</Label>
            <Input 
              type="date" 
              value={formData.termination_date} 
              onChange={(e) => handleSourceDateChange('termination_date', e.target.value)} 
              data-testid="employee-termination-date-input"
            />
          </div>
        )}
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label>Last Working Date</Label>
            <div className="flex items-center gap-1.5">
              <Checkbox
                id="lwd-manual"
                checked={isManual}
                onCheckedChange={handleManualToggle}
                data-testid="lwd-manual-checkbox"
              />
              <label htmlFor="lwd-manual" className="text-xs text-muted-foreground cursor-pointer select-none">
                Set manually
              </label>
            </div>
          </div>
          <Input 
            type="date" 
            value={formData.last_working_date} 
            onChange={(e) => handleChange('last_working_date', e.target.value)} 
            disabled={!isManual}
            className={!isManual ? 'bg-muted' : ''}
            data-testid="employee-last-working-date-input"
          />
          {!isManual && sourceDate && (
            <p className="text-xs text-muted-foreground mt-1">
              Auto-calculated: 30 days from {formData.employment_status === 'Resigned' ? 'resignation' : 'termination'} date
            </p>
          )}
        </div>
        <div className="md:col-span-2">
          <Label>Exit Reason</Label>
          <Input 
            value={formData.exit_reason} 
            onChange={(e) => handleChange('exit_reason', e.target.value)} 
            placeholder="Reason for leaving..."
            data-testid="employee-exit-reason-input"
          />
        </div>
        <div className="md:col-span-2 rounded-lg border border-rose-200 bg-rose-50 p-3">
          <h4 className="text-sm font-semibold text-rose-700 mb-2">Visa Cancellation</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Visa Cancellation Date</Label>
              <Input
                type="date"
                value={formData.visa_cancelled_date || ''}
                onChange={(e) => handleChange('visa_cancelled_date', e.target.value)}
                data-testid="employee-visa-cancellation-date-input"
              />
            </div>
            <div>
              <Label>Visa Cancellation Reason</Label>
              <Input
                value={formData.visa_cancellation_reason || ''}
                onChange={(e) => handleChange('visa_cancellation_reason', e.target.value)}
                placeholder="e.g. Returned home, Transferred"
                data-testid="employee-visa-cancellation-reason-input"
              />
            </div>
          </div>
          <p className="text-[11px] text-rose-600 mt-2">Recorded here appears in the Cancelled Visa Report.</p>
        </div>
      </div>
    </div>
  );
};

export default ExitInfoSection;
